/**
 * How a sent user message is shown in the thread, in the style of
 * `views/registry.ts` and `views/messageFooters.ts`: a package whose flow
 * sends a message on the user's behalf (e.g. P4's product-selection takeover
 * note) registers a display that turns its raw text into what the user
 * should read — one import, one entry. Displays only change what is shown;
 * the message sent and stored stays as it is. Empty in the foundation.
 */
import type { TFunction } from "@msys/tolgee";
import { stripAgentContextBlock } from "../agentContextSpec.js";
import { stripFormAnswerBlock } from "./agentFormSpec.js";
import { displayProductSelectionNote } from "./productSelectionDisplay.js";

/**
 * Turns a user message's visible text into the text to show; returns it
 * unchanged when it is not its own. `t` is the panel's own translate
 * function (namespace "Assistant"), for a display that shows a translated
 * text instead of the raw one.
 */
export type AgentUserMessageDisplay = (
  text: string,
  t: TFunction<"Assistant">
) => string;

export const agentUserMessageDisplays: AgentUserMessageDisplay[] = [
  displayProductSelectionNote,
];

/**
 * A user message as shown: the form-answer and AI-button context machine
 * blocks removed first, then every registered display applied in registry
 * order, each to the previous one's result, each with the panel's `t`. Pure.
 */
export function agentUserMessageText(
  text: string,
  t: TFunction<"Assistant">,
  displays: readonly AgentUserMessageDisplay[] = agentUserMessageDisplays
): string {
  return displays.reduce(
    (shown, display) => display(shown, t),
    stripAgentContextBlock(stripFormAnswerBlock(text))
  );
}
